import React from 'react';
import { toast } from 'sonner';

import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { BaseMatterNote } from '@/services/types/matter-notes-types';
import { MedicalProvider } from '@/services/types/treatment-types';

import { MatterNoteConfig } from '../types';
import { BaseMatterNoteForm } from './BaseMatterNoteForm';
import { MedicalProviderTasksDropdown } from './components/MedicalProviderTasksDropdown';

export interface MedicalProviderNoteFormProps {
  config: MatterNoteConfig;
  matterId: string;
  note?: MedicalProvider | null;
  onSaved?: (note: MedicalProvider) => void;
  onCancelled?: () => void;
  onDeleted?: () => void;
}

/**
 * Custom hook to manage treatment record request creation
 */
const useTreatmentRecordRequestCreation = (matterId: string) => {
  const [createTreatmentRecordRequest] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsCreateMutation();

  /**
   * Creates a treatment record request for a provider
   */
  const createRecordRequestForProvider = async (provider: MedicalProvider) => {
    try {
      await createTreatmentRecordRequest({
        matterId,
        providerId: provider.id,
        createTreatmentRecordRequestSchema: {
          actor: 'user',
          status: 'pending',
          provided_records: ['Bills', 'Records'],
          channel: {
            type: 'fax',
            fax: '',
          },
        },
      }).unwrap();

      toast.success('Created records request for provider');
    } catch (error) {
      console.error('Failed to create treatment record request:', error);
      const data = (error as { data?: unknown })?.data;
      let errorMessage: string;
      if (Array.isArray(data)) {
        errorMessage = data
          .map((item) =>
            typeof item === 'string'
              ? item
              : item?.msg || item?.detail || JSON.stringify(item),
          )
          .join('; ');
      } else {
        errorMessage =
          (data as { detail?: string })?.detail ||
          (data as { message?: string })?.message ||
          (error as { message?: string })?.message ||
          '';
      }

      if (errorMessage.includes('workflow is not enabled')) {
        toast.error(
          'Medical Records workflow is not enabled for this matter',
          {
            description:
              'This matter is missing the Medical Records scope of work. ' +
              'Please escalate to #app-feedback on Slack for assistance.',
            duration: 10000,
          },
        );
      } else {
        toast.error('Failed to create records request');
      }
      throw error;
    }
  };

  return { createRecordRequestForProvider };
};

export const MedicalProviderNoteForm: React.FC<
  MedicalProviderNoteFormProps
> = ({ config, matterId, note, onSaved, onCancelled, onDeleted }) => {
  const isNew = !note?.id;
  const { createRecordRequestForProvider } =
    useTreatmentRecordRequestCreation(matterId);

  /**
   * Handles record request creation from the dropdown (for existing providers)
   */
  const handleDropdownCreateRecordRequest = async () => {
    if (!note) {
      toast.error('Cannot create record request without a medical provider');
      return;
    }

    await createRecordRequestForProvider(note as MedicalProvider);
  };

  /**
   * Handles form submission and record request creation
   */
  const handleNoteFormSaved = (savedNote: MedicalProvider) => {
    // Always call onSaved first to ensure the note is completely saved
    onSaved?.(savedNote);

    // Create a treatment record request for new providers
    if (isNew) {
      createRecordRequestForProvider(savedNote).catch((error) => {
        console.error(
          'Failed to create record request after saving note:',
          error,
        );
      });
    }
  };

  /**
   * Renders extra actions in the form (dropdown for existing providers)
   */
  const renderExtraActions = (noteId?: string) => {
    if (!noteId) return null;

    return (
      <MedicalProviderTasksDropdown
        noteId={noteId}
        createTasks={handleDropdownCreateRecordRequest}
      />
    );
  };

  /**
   * Renders extra content in the form
   * For new providers, we no longer show task selection since TRR handles both Bills and Records
   */
  const renderExtraContent = () => {
    // No extra content needed - TRR creation is automatic for new providers
    return null;
  };

  return (
    <BaseMatterNoteForm
      config={config}
      matterId={matterId}
      note={note}
      onSaved={(savedNote: BaseMatterNote) =>
        handleNoteFormSaved(savedNote as MedicalProvider)
      }
      onCancelled={onCancelled}
      onDeleted={onDeleted}
      renderExtraActions={renderExtraActions}
      renderExtraContent={renderExtraContent}
    />
  );
};
